import { RefObject, useCallback, useState } from "react";
import classNames from "classnames";
import { setSplitDragging } from "./splitDragState";

const STORAGE_KEY = "sequenceMapSplit";
const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;
const KEYBOARD_STEP = 0.05;

const clampRatio = (ratio: number) =>
  Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));

function readStoredRatio() {
  const stored = Number(localStorage.getItem(STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampRatio(stored) : 0.5;
}

/** How much of the viewport the sequence canvas takes in the split; remembered between sessions. */
export function useViewportSplitRatio() {
  const [ratio, setRatio] = useState(readStoredRatio);
  const update = useCallback((next: number) => {
    const clamped = Math.round(clampRatio(next) * 1000) / 1000;
    setRatio(clamped);
    localStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);
  return [ratio, update] as const;
}

/**
 * The bar between the sequence canvas and the map. Without a map it sits at
 * the bottom of the sequence; dragging it up opens one. Works in ratios of the
 * container, so the editor's CSS zoom (screen scaling) needs no conversion.
 * `onResize` and `onDragEnd` get the raw ratio, so the caller can open or
 * close the map at the edges.
 */
export default function ViewportSplitHandle(props: {
  containerRef: RefObject<HTMLElement>;
  isOpen: boolean;
  ratio: number;
  onResize: (ratio: number) => void;
  onDragEnd: (ratio: number) => void;
}) {
  const { containerRef, isOpen, ratio, onResize, onDragEnd } = props;
  const [isDragging, setIsDragging] = useState(false);

  const ratioAt = (clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return ratio;
    return (clientY - rect.top) / rect.height;
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={isOpen ? "Resize the sequence and the map" : "Drag up to open the map"}
      aria-valuemin={MIN_RATIO * 100}
      aria-valuemax={100}
      aria-valuenow={isOpen ? Math.round(ratio * 100) : 100}
      tabIndex={0}
      title={isOpen ? undefined : "Drag up to open the map"}
      data-testid="viewport3d-split-handle"
      className={classNames("viewport3d-split-handle", {
        "viewport3d-split-handle--collapsed": !isOpen,
        "viewport3d-split-handle--dragging": isDragging,
      })}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsDragging(true);
        setSplitDragging(true);
      }}
      onPointerMove={(event) => {
        if (isDragging) onResize(ratioAt(event.clientY));
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        setIsDragging(false);
        onDragEnd(ratioAt(event.clientY));
        setSplitDragging(false);
      }}
      onPointerCancel={() => {
        setIsDragging(false);
        setSplitDragging(false);
      }}
      onKeyDown={(event) => {
        // Collapsed, ArrowUp opens the map at the remembered size.
        if (event.key === "ArrowUp") onResize(isOpen ? ratio - KEYBOARD_STEP : ratio);
        else if (event.key === "ArrowDown" && isOpen) onResize(ratio + KEYBOARD_STEP);
        else return;
        event.preventDefault();
      }}
    />
  );
}
